import { GQL } from "@binale-tech/shared";

import { filingCovers } from "@accounting/common/metrics/vatReconciliation";

import { withThinking } from "../runner/runAgent";
import type {
    AgentContext,
    AgentReportData,
    AgentStep,
    AgentStepId,
    AgentStepReport,
    ReportLine,
} from "../runner/types";

const PENDING_KINDS: Partial<Record<GQL.IPendingBucketKind, ReportLine["textId"]>> = {
    [GQL.IPendingBucketKind.DocumentsNeedAnswer]: "app.agent.report.pending.DOCUMENTS_NEED_ANSWER",
    [GQL.IPendingBucketKind.DocumentsNotProcessed]: "app.agent.report.pending.DOCUMENTS_NOT_PROCESSED",
    [GQL.IPendingBucketKind.DocumentsReadyToBook]: "app.agent.report.pending.DOCUMENTS_READY_TO_BOOK",
    [GQL.IPendingBucketKind.BankTransactionsNotBooked]: "app.agent.report.pending.BANK_TRANSACTIONS_NOT_BOOKED",
    [GQL.IPendingBucketKind.KasseTransactionsNotBooked]: "app.agent.report.pending.KASSE_TRANSACTIONS_NOT_BOOKED",
    [GQL.IPendingBucketKind.AiDraftsToReview]: "app.agent.report.pending.AI_DRAFTS_TO_REVIEW",
    [GQL.IPendingBucketKind.DraftsWithoutAccount]: "app.agent.report.pending.DRAFTS_WITHOUT_ACCOUNT",
};

const pendingLines = (buckets: GQL.IPendingBucket[]): ReportLine[] => {
    const totals = new Map<GQL.IPendingBucketKind, number>();
    for (const b of buckets) totals.set(b.kind, (totals.get(b.kind) ?? 0) + b.count);
    return Object.entries(PENDING_KINDS).flatMap(([kind, textId]) => {
        const count = totals.get(kind as GQL.IPendingBucketKind) ?? 0;
        return count > 0 && textId ? [{ textId, values: { count } }] : [];
    });
};

/**
 * The UStVA period the agent checks: the latest period whose due date (the 10th after the period, plus one month with
 * Dauerfristverlaengerung) has passed. A period not yet due is not reported; a future year checks nothing.
 */
const ustvaPeriod = (
    frequency: GQL.IUstvaFilingFrequency,
    year: number,
    today: Date,
    extensionMonths: number,
): number | null => {
    const quarterly = frequency === GQL.IUstvaFilingFrequency.Quarterly;
    const perYear = quarterly ? 4 : 12;
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    for (let n = perYear; n >= 1; n--) {
        const endMonth = quarterly ? n * 3 : n;
        if (day > new Date(year, endMonth + extensionMonths, 10)) return quarterly ? 40 + n : n;
    }
    return null;
};

const ustvaLine = async (ctx: AgentContext): Promise<ReportLine[]> => {
    const frequency = ctx.ustvaFrequency();
    if (!frequency || frequency === GQL.IUstvaFilingFrequency.None) return [];
    const period = ustvaPeriod(frequency, ctx.year, new Date(), ctx.ustvaDauerfrist() ? 1 : 0);
    if (period === null) return [];
    try {
        const exports = await ctx.readElsterExports();
        if (exports.some((e) => e.ericResultCode === 0 && filingCovers(e.period, period))) return [];
    } catch (error) {
        console.error("Agent report: elster exports read failed", error);
        return [];
    }
    const label = period > 40 ? `Q${period - 40}` : String(period).padStart(2, "0");
    return [{ textId: "app.agent.report.ustvaOpen", values: { period: `${label}/${ctx.year}` } }];
};

const draftProduct = (ctx: AgentContext): GQL.IProductKey | undefined => {
    const counts = new Map<GQL.IProductKey, number>();
    for (const id of ctx.created.recordIds) {
        const product = ctx.recordInfo(id)?.product;
        if (product) counts.set(product, (counts.get(product) ?? 0) + 1);
    }
    let best: GQL.IProductKey | undefined;
    for (const [product, count] of counts) if (count > (counts.get(best as GQL.IProductKey) ?? 0)) best = product;
    return best;
};

export const reportStep: AgentStep = {
    id: "report",
    async run(ctx, io) {
        const [buckets, ustva] = await withThinking(io, "app.agent.report.thinking", () =>
            Promise.all([
                ctx.readPendingBuckets().catch((error: unknown): GQL.IPendingBucket[] => {
                    console.error("Agent report: pendingWork read failed", error);
                    return [];
                }),
                ustvaLine(ctx),
            ]),
        );

        const results = (Object.entries(ctx.runReport.steps) as [AgentStepId, AgentStepReport | undefined][]).filter(
            ([id]) => id !== "report",
        );
        const done = results.flatMap(([, r]) => r?.summary ?? []);
        const continuing = results.flatMap(([id, r]): ReportLine[] =>
            r?.continuesOnServer
                ? [{ textId: "app.agent.report.continuesInBinale", labels: { step: `app.agent.step.${id}` } }]
                : [],
        );
        const open = [...results.flatMap(([, r]) => r?.open ?? []), ...continuing, ...pendingLines(buckets), ...ustva];

        const report: AgentReportData = { year: ctx.year, done, open, draftProduct: draftProduct(ctx) };
        io.post({ kind: "report", textId: "app.agent.report.title", report });
        return { status: "done" };
    },
};
